import { Employee, Candidate, Interview } from '../types/hr.types'

// Helper to generate dates relative to today
const today = new Date()
const tomorrow = new Date(today)
tomorrow.setDate(today.getDate() + 1)
const yesterday = new Date(today)
yesterday.setDate(today.getDate() - 1)
const nextDay = new Date(today)
nextDay.setDate(today.getDate() + 2)
const nextWeek = new Date(today)
nextWeek.setDate(today.getDate() + 7)
const nextMonth = new Date(today)
nextMonth.setDate(today.getDate() + 15)

const formatDate = (d: Date) => d.toISOString().split('T')[0]
const formatMMDD = (d: Date) =>
  `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
// Build a local ISO datetime string (yyyy-MM-ddTHH:mm) for a given day + time.
// Used so mock candidates have realistic scheduled times.
const formatDateTime = (d: Date, hour: number, minute: number) => {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(hour).padStart(2, '0')
  const mn = String(minute).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T${hh}:${mn}`
}

export const mockEmployees: Employee[] = [
  {
    id: 'e1',
    name: 'Sarah Jenkins',
    department: 'Engineering',
    photoUrl:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    birthday: formatMMDD(today), // Birthday today
    isActive: true,
  },
  {
    id: 'e2',
    name: 'Michael Chen',
    department: 'Product',
    photoUrl:
      'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    birthday: formatMMDD(tomorrow), // Birthday tomorrow
    isActive: true,
  },
  {
    id: 'e3',
    name: 'Emily Davis',
    department: 'Marketing',
    photoUrl:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    birthday: formatMMDD(nextDay), // Birthday this week
    isActive: true,
  },
  {
    id: 'e4',
    name: 'David Wilson',
    department: 'Sales',
    photoUrl:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    birthday: '05-15', // Random date
    isActive: true,
  },
  {
    id: 'e5',
    name: 'Jessica Taylor',
    department: 'HR',
    photoUrl:
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    birthday: formatMMDD(today), // Another birthday today
    isActive: true,
  },
]

export const mockCandidates: Candidate[] = [
  {
    id: 'c1',
    firstName: 'Alex',
    lastName: 'Johnson',
    role: 'Team Lead',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(today, 9, 0),
    email: 'alex.j@example.com',
    phone: '(555) 123-4567',
    status: 'Ready to Start',
    branch: 'East',
    interviewedBy: 'Kaylin Stepho',
    recruiter: 'Kaylin Stepho',
    candidateSource: 'LinkedIn',
  },
  {
    id: 'c2',
    firstName: 'Maria',
    lastName: 'Rodriguez',
    role: 'Loan Officer',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(tomorrow, 10, 30),
    email: 'maria.r@example.com',
    phone: '(555) 987-6543',
    status: 'Pre-boarding',
    branch: 'West',
    interviewedBy: 'Zeinab Haidar',
    recruiter: 'Zeinab Haidar',
    candidateSource: 'Indeed',
  },
  {
    id: 'c3',
    firstName: 'James',
    lastName: 'Smith',
    role: 'Loan Partner',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(nextDay, 11, 0),
    inPersonInterviewDate: formatDateTime(nextDay, 14, 0),
    email: 'james.s@example.com',
    phone: '(555) 456-7890',
    status: 'Offer Accepted',
    branch: 'West',
    interviewedBy: 'Kaylin Stepho',
    recruiter: 'Kaylin Stepho',
    candidateSource: 'Referral',
    referredBy: 'Sarah Johnson',
  },
  {
    id: 'c4',
    firstName: 'Emily',
    lastName: 'Chen',
    role: 'Processor',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(today, 13, 0),
    email: 'emily.c@example.com',
    phone: '(555) 234-5678',
    status: 'Phone Screen',
    branch: 'East',
    interviewedBy: 'Zeinab Haidar',
    recruiter: 'Zeinab Haidar',
    candidateSource: 'LinkedIn',
  },
  {
    id: 'c5',
    firstName: 'Michael',
    lastName: 'Brown',
    role: 'Telemarketer',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(nextWeek, 15, 30),
    email: 'michael.b@example.com',
    phone: '(555) 876-5432',
    status: 'Waiting on Response',
    branch: 'West',
    interviewedBy: 'Kaylin Stepho',
    recruiter: 'Kaylin Stepho',
    candidateSource: 'Indeed',
    offerSentDate: formatDateTime(yesterday, 16, 0),
    offerAmount: 65000,
  },
  {
    id: 'c6',
    firstName: 'Sarah',
    lastName: 'Wilson',
    role: 'Admin',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(nextMonth, 10, 0),
    email: 'sarah.w@example.com',
    phone: '(555) 345-6789',
    status: 'Follow Up Later',
    branch: 'East',
    interviewedBy: 'Zeinab Haidar',
    recruiter: 'Zeinab Haidar',
    candidateSource: 'Referral',
    referredBy: 'Mike Williams',
    followUpDate: formatDateTime(today, 14, 30),
    followUpReason: 'Check in on counter-offer from current employer',
  },
  {
    id: 'c7',
    firstName: 'David',
    lastName: 'Lee',
    role: 'Processing Manager',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(today, 9, 30),
    email: 'david.l@example.com',
    phone: '(555) 432-1098',
    status: 'Follow Up Later',
    branch: 'East',
    interviewedBy: 'Kaylin Stepho',
    recruiter: 'Kaylin Stepho',
    candidateSource: 'LinkedIn',
    followUpDate: formatDateTime(yesterday, 11, 0),
    followUpReason: 'Candidate asked to reconnect in Q3',
  },
  {
    id: 'c8',
    firstName: 'Jessica',
    lastName: 'Martinez',
    role: 'Loan Officer',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(yesterday, 12, 0),
    email: 'jessica.m@example.com',
    phone: '(555) 555-1212',
    status: 'Phone Screen',
    branch: 'West',
    interviewedBy: 'Zeinab Haidar',
    recruiter: 'Zeinab Haidar',
    candidateSource: 'Indeed',
  },
  {
    id: 'c9',
    firstName: 'Robert',
    lastName: 'Taylor',
    role: 'Processor',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(yesterday, 15, 0),
    email: 'robert.t@example.com',
    phone: '(555) 999-8888',
    status: 'Hired Elsewhere',
    branch: 'East',
    interviewedBy: 'Kaylin Stepho',
    recruiter: 'Kaylin Stepho',
    candidateSource: 'LinkedIn',
    followUpDate: formatDateTime(nextWeek, 10, 0),
    followUpReason: 'Check if new role is working out',
  },
  {
    id: 'c10',
    firstName: 'Brian',
    lastName: 'Adams',
    role: 'Loan Officer',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(
      new Date(today.getTime() - 40 * 86400000),
      10,
      0,
    ),
    email: 'brian.a@example.com',
    phone: '(555) 111-2222',
    status: 'Offer Declined',
    branch: 'East',
    interviewedBy: 'Mirna Hermiz',
    recruiter: 'Mirna Hermiz',
    candidateSource: 'LinkedIn',
    offerSentDate: formatDateTime(
      new Date(today.getTime() - 20 * 86400000),
      14,
      0,
    ),
    offerDecisionDate: formatDateTime(
      new Date(today.getTime() - 18 * 86400000),
      9,
      0,
    ),
    offerAmount: 55000,
    declineReason: 'Salary too low',
  },
  {
    id: 'c11',
    firstName: 'Lisa',
    lastName: 'Wong',
    role: 'Loan Officer',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(
      new Date(today.getTime() - 35 * 86400000),
      11,
      30,
    ),
    email: 'lisa.w@example.com',
    phone: '(555) 222-3333',
    status: 'Offer Declined',
    branch: 'West',
    interviewedBy: 'Mirna Hermiz',
    recruiter: 'Mirna Hermiz',
    candidateSource: 'Referral',
    offerSentDate: formatDateTime(
      new Date(today.getTime() - 15 * 86400000),
      16,
      0,
    ),
    offerDecisionDate: formatDateTime(
      new Date(today.getTime() - 12 * 86400000),
      10,
      0,
    ),
    offerAmount: 62000,
    declineReason: 'Took counter-offer',
  },
  {
    id: 'c12',
    firstName: 'Kevin',
    lastName: 'Patel',
    role: 'Processor',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(
      new Date(today.getTime() - 30 * 86400000),
      14,
      0,
    ),
    email: 'kevin.p@example.com',
    phone: '(555) 333-4444',
    status: 'Offer Declined',
    branch: 'East',
    interviewedBy: 'Mirna Hermiz',
    recruiter: 'Mirna Hermiz',
    candidateSource: 'Indeed',
    offerSentDate: formatDateTime(
      new Date(today.getTime() - 10 * 86400000),
      11,
      0,
    ),
    offerDecisionDate: formatDateTime(
      new Date(today.getTime() - 8 * 86400000),
      15,
      0,
    ),
    offerAmount: 48000,
    declineReason: 'Other opportunity',
  },
  {
    id: 'c13',
    firstName: 'Amanda',
    lastName: 'Jones',
    role: 'Telemarketer',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(
      new Date(today.getTime() - 25 * 86400000),
      9,
      30,
    ),
    email: 'amanda.j@example.com',
    phone: '(555) 444-5555',
    status: 'Offer Declined',
    branch: 'East',
    interviewedBy: 'Mirna Hermiz',
    recruiter: 'Mirna Hermiz',
    candidateSource: 'Social Media',
    offerSentDate: formatDateTime(
      new Date(today.getTime() - 12 * 86400000),
      13,
      0,
    ),
    offerDecisionDate: formatDateTime(
      new Date(today.getTime() - 10 * 86400000),
      11,
      0,
    ),
    offerAmount: 42000,
    declineReason: 'Personal reasons',
  },
  {
    id: 'c14',
    firstName: 'Eric',
    lastName: 'Davis',
    role: 'Team Lead',
    department: 'Operations',
    phoneInterviewDate: formatDateTime(
      new Date(today.getTime() - 45 * 86400000),
      13,
      30,
    ),
    email: 'eric.d@example.com',
    phone: '(555) 555-6666',
    status: 'Offer Declined',
    branch: 'West',
    interviewedBy: 'Mirna Hermiz',
    recruiter: 'Mirna Hermiz',
    candidateSource: 'LinkedIn',
    offerSentDate: formatDateTime(
      new Date(today.getTime() - 25 * 86400000),
      15,
      0,
    ),
    offerDecisionDate: formatDateTime(
      new Date(today.getTime() - 22 * 86400000),
      14,
      0,
    ),
    offerAmount: 95000,
    declineReason: 'Relocation',
  },
  {
    id: 'c15',
    firstName: 'Rachel',
    lastName: 'Kim',
    role: 'Loan Partner',
    department: 'Sales',
    phoneInterviewDate: formatDateTime(
      new Date(today.getTime() - 20 * 86400000),
      10,
      30,
    ),
    email: 'rachel.k@example.com',
    phone: '(555) 666-7777',
    status: 'Offer Declined',
    branch: 'East',
    interviewedBy: 'Mirna Hermiz',
    recruiter: 'Mirna Hermiz',
    candidateSource: 'Referral',
    offerSentDate: formatDateTime(
      new Date(today.getTime() - 5 * 86400000),
      10,
      0,
    ),
    offerDecisionDate: formatDateTime(
      new Date(today.getTime() - 2 * 86400000),
      16,
      0,
    ),
    offerAmount: 75000,
    declineReason: 'Salary too low',
  },
]

export const mockInterviews: Interview[] = [
  {
    id: 'i1',
    candidateName: 'Robert Brown',
    candidateId: 'c_pot_1',
    type: 'Phone Screen',
    dateTime: new Date(today.setHours(10, 0, 0, 0)).toISOString(), // Today 10am
    interviewer: 'Jessica Taylor',
    role: 'Customer Support Lead',
  },
  {
    id: 'i2',
    candidateName: 'Linda White',
    candidateId: 'c_pot_2',
    type: 'In-Person',
    dateTime: new Date(today.setHours(14, 30, 0, 0)).toISOString(), // Today 2:30pm
    interviewer: 'Sarah Jenkins',
    role: 'UX Designer',
  },
  {
    id: 'i3',
    candidateName: 'Thomas Anderson',
    candidateId: 'c_pot_3',
    type: 'Video Call',
    dateTime: new Date(tomorrow.setHours(11, 0, 0, 0)).toISOString(), // Tomorrow 11am
    interviewer: 'Michael Chen',
    role: 'Backend Engineer',
  },
  {
    id: 'i4',
    candidateName: 'Patricia Martin',
    candidateId: 'c_pot_4',
    type: 'Phone Screen',
    dateTime: new Date(nextDay.setHours(15, 0, 0, 0)).toISOString(), // Next day 3pm
    interviewer: 'Jessica Taylor',
    role: 'HR Coordinator',
  },
]
